import { Button, Divider } from "@fluentui/react-components";
import { AddRegular, DeleteRegular, EditRegular } from "@fluentui/react-icons";
import { AdminListPage } from "components/adminList/AdminListPage";
import { IAdminRowAction } from "components/adminList/AdminListPage.types";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { IStakeholder } from "models/IProjectIQWorkspace";
import { ITableColumn } from "models/ITableColumn";
import { useMemo, useState } from "react";
import { AddStakeholderDialog } from "./AddStakeholderDialog";
import { useStakeholdersSectionStyles } from "./StakeholdersSection.styles";
import { IStakeholdersSectionProps } from "./StakeholdersSection.types";

const ACTIONS_COLUMN_WIDTH = "96px";

export const StakeholdersSection = ({
  stakeholders,
  canEdit,
  onStakeholdersChange,
}: IStakeholdersSectionProps): JSX.Element => {
  const styles = useStakeholdersSectionStyles();
  const colStyles = useAdminColumnStyles();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingStakeholder, setEditingStakeholder] = useState<
    IStakeholder | undefined
  >(undefined);
  const [deletePendingStakeholder, setDeletePendingStakeholder] =
    useState<IStakeholder | null>(null);

  const handleAddStakeholder = () => {
    setEditingStakeholder(undefined);
    setIsDialogOpen(true);
  };

  const handleEditStakeholder = (stakeholder: IStakeholder) => {
    setEditingStakeholder(stakeholder);
    setIsDialogOpen(true);
  };

  const handleDeleteStakeholder = (stakeholder: IStakeholder) => {
    onStakeholdersChange(stakeholders.filter((s) => s.id !== stakeholder.id));
  };

  const handleDialogConfirm = (stakeholder: IStakeholder) => {
    const exists = stakeholders.some((s) => s.id === stakeholder.id);
    const next = exists
      ? stakeholders.map((s) => (s.id === stakeholder.id ? stakeholder : s))
      : [...stakeholders, stakeholder];
    onStakeholdersChange(next);
    setIsDialogOpen(false);
  };

  const handleDialogDismiss = () => {
    setIsDialogOpen(false);
    setEditingStakeholder(undefined);
  };

  const STAKEHOLDER_COLUMNS = useMemo<ITableColumn<IStakeholder>[]>(
    () => [
      {
        columnId: "name",
        displayValue: "Name",
        className: colStyles.normal,
        compare: (a, b) => a.name.localeCompare(b.name),
        renderHeaderCell: () => "Name",
        renderCell: (item) => item.name,
      },
      {
        columnId: "roleTitle",
        displayValue: "Role / Title",
        className: colStyles.normal,
        compare: (a, b) => a.roleTitle.localeCompare(b.roleTitle),
        renderHeaderCell: () => "Role / Title",
        renderCell: (item) => item.roleTitle,
      },
      {
        columnId: "actions",
        displayValue: "Details",
        className: colStyles.wide,
        compare: (a, b) => (a.details ?? "").localeCompare(b.details ?? ""),
        renderHeaderCell: () => "Details",
        renderCell: (item) => item.details ?? "—",
      },
    ],
    [colStyles],
  );

  const rowActions = useMemo<IAdminRowAction<IStakeholder>[]>(
    () =>
      canEdit
        ? [
            {
              icon: <EditRegular aria-hidden="true" />,
              tooltip: "Edit stakeholder",
              onClick: handleEditStakeholder,
            },
            {
              icon: <DeleteRegular aria-hidden="true" />,
              tooltip: "Delete stakeholder",
              onClick: setDeletePendingStakeholder,
            },
          ]
        : [],
    [canEdit],
  );

  return (
    <div className={styles.root}>
      <Divider className={styles.divider} />
      <div className={styles.header}>
        <h2 className={styles.sectionTitle}>Stakeholders</h2>
        <Button
          appearance="primary"
          icon={<AddRegular aria-hidden="true" />}
          onClick={handleAddStakeholder}
          aria-label="Add stakeholder"
          disabled={!canEdit}
        >
          Add stakeholder
        </Button>
      </div>
      <div className={styles.adminListWrapper}>
        <AdminListPage
          items={stakeholders}
          columns={STAKEHOLDER_COLUMNS}
          rowActions={rowActions}
          getRowId={(item) => item.id}
          emptyStateMessage="No stakeholders added yet. Add key client and project stakeholders to keep your team aligned."
          actionsWidth={ACTIONS_COLUMN_WIDTH}
        />
      </div>
      <AddStakeholderDialog
        isOpen={isDialogOpen}
        onDismiss={handleDialogDismiss}
        onConfirm={handleDialogConfirm}
        initialValues={editingStakeholder}
      />
      <ActionDialog
        isVisible={deletePendingStakeholder !== null}
        title="Delete stakeholder?"
        message={
          deletePendingStakeholder
            ? `Are you sure you want to delete "${deletePendingStakeholder.name}"? This cannot be undone.`
            : "Are you sure you want to delete this stakeholder? This cannot be undone."
        }
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        onConfirmation={() => {
          if (deletePendingStakeholder) {
            handleDeleteStakeholder(deletePendingStakeholder);
            setDeletePendingStakeholder(null);
          }
        }}
        onDismiss={() => setDeletePendingStakeholder(null)}
      />
    </div>
  );
};
